import type { RecipeFamily } from "../../domain/types";
import { option, pantry, slot } from "./helpers";

export const saladRecipes: RecipeFamily[] = [
  {
    id: "cucumber-tomato-herb-salad",
    nameTemplate: "{vegetable.name}, {base.name} & {herb.name} salad",
    foodType: "salad",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 10,
    slots: [
      slot("vegetable", "Crisp vegetable", true, "cucumber", [
        option("cucumber", 2, "piece", "thinly sliced", 3, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
        option("zucchini", 300, "g", "very thinly sliced", 2, undefined, {
          tr: { preparation: "çok ince dilimlenmiş" },
        }),
      ]),
      slot("base", "Juicy base", true, "tomato", [
        option("tomato", 400, "g", "cut into wedges", 3, undefined, {
          tr: { preparation: "dilim şeklinde kesilmiş" },
        }),
        option("bell-pepper", 350, "g", "thinly sliced", 2, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
      ]),
      slot("herb", "Fresh herb", true, "parsley", [
        option("parsley", 20, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "kabaca doğranmış" },
        }),
        option("mint", 15, "g", "roughly chopped", 2, undefined, {
          tr: { preparation: "kabaca doğranmış" },
        }),
      ]),
      slot("richness", "Richness", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
        option("avocado", 1, "piece", "sliced", 2, undefined, {
          tr: { preparation: "dilimlenmiş" },
        }),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu çıkarılmış" },
        }),
        option("balsamic-vinegar", 1, "tbsp", undefined, 2),
      ]),
      slot("finish", "Finish", false, "sumac", [
        option("sumac", 1, "tsp", undefined, 3),
        option("chili-flakes", 1, "pinch", undefined, 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "combine",
        template:
          "Combine {vegetable.name}, {base.name}, and {herb.name} in a large bowl.",
        translations: {
          tr: {
            template:
              "{vegetable.name}, {base.name} ve {herb.name} büyük bir kaseye alın.",
          },
        },
      },
      {
        id: "dress",
        template:
          "Add {richness.name}, {acid.name}, {salt.name}, and {black-pepper.name}. Toss gently to coat.",
        translations: {
          tr: {
            template:
              "{richness.name}, {acid.name}, {salt.name} ve {black-pepper.name} ekleyin. Hafifçe karıştırın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Scatter {finish.name} over the top if using and serve immediately.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} üzerine serpin ve hemen servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["fresh", "acid", "fat", "aroma", "bulk"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{vegetable.name}, {base.name} & {herb.name} salatası",
      },
    },
  },
  {
    id: "bulgur-roasted-pepper-salad",
    nameTemplate: "{grain.name} & {vegetable.name} salad with {acid.name}",
    foodType: "salad",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 30,
    slots: [
      slot("grain", "Grain base", true, "bulgur", [
        option("bulgur", 180, "g", "cooked", 3, undefined, {
          tr: { preparation: "pişirilmiş" },
        }),
        option("couscous", 180, "g", "cooked", 2, undefined, {
          tr: { preparation: "pişirilmiş" },
        }),
      ]),
      slot("vegetable", "Roasted vegetable", true, "bell-pepper", [
        option("bell-pepper", 350, "g", "seeded and quartered", 3, undefined, {
          tr: { preparation: "tohumları çıkarılmış ve dörde bölünmüş" },
        }),
        option("zucchini", 350, "g", "cut into thick rounds", 2, undefined, {
          tr: { preparation: "kalın halkalar halinde kesilmiş" },
        }),
      ]),
      slot("protein", "Protein", false, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3, undefined, {
          tr: { preparation: "süzülmüş" },
        }),
        option("feta", 100, "g", "crumbled", 2, undefined, {
          tr: { preparation: "ufalanmış" },
        }),
      ]),
      slot("herb", "Fresh herb", true, "parsley", [
        option("parsley", 20, "g", "chopped", 3, undefined, {
          tr: { preparation: "doğranmış" },
        }),
        option("mint", 15, "g", "chopped", 2, undefined, {
          tr: { preparation: "doğranmış" },
        }),
      ]),
      slot("acid", "Dressing acid", true, "pomegranate-molasses", [
        option("pomegranate-molasses", 2, "tbsp", undefined, 3),
        option("lemon", 1, "piece", "juiced", 2, undefined, {
          tr: { preparation: "suyu çıkarılmış" },
        }),
      ]),
      slot("oil", "Oil", true, "olive-oil", [option("olive-oil", 2, "tbsp")]),
      slot("finish", "Finish", false, "sumac", [
        option("sumac", 1, "tsp", undefined, 3),
        option("pomegranate", 50, "g", "seeds", 2, undefined, {
          tr: { preparation: "taneleri" },
        }),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "roast-veg",
        template:
          "Toss {vegetable.name} with {oil.name}, {salt.name}, and {black-pepper.name}. Char directly over a gas flame or under a hot grill for 10 minutes, turning occasionally, until blistered. Peel if desired, then slice.",
        translations: {
          tr: {
            template:
              "{vegetable.name} üzerine {oil.name}, {salt.name} ve {black-pepper.name} gezdirin. Gaz alevi üzerinde ya da kızgın ızgarada zaman zaman çevirerek kabarcıklanana kadar 10 dakika kavurun. İsterseniz kabuğunu soyun, ardından dilimleyin.",
          },
        },
      },
      {
        id: "dress-grain",
        template:
          "Combine {grain.name} with {acid.name}, {oil.name}, {salt.name}, and {black-pepper.name}. Fold in {protein.name} if using.",
        translations: {
          tr: {
            template:
              "{grain.name} üzerine {acid.name}, {oil.name}, {salt.name} ve {black-pepper.name} ekleyin. Kullanıyorsanız {protein.name} karıştırın.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Fold in {vegetable.name} and {herb.name}. Taste and adjust the dressing.",
        translations: {
          tr: {
            template:
              "{vegetable.name} ve {herb.name} ekleyin. Tadına bakıp sosu ayarlayın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Scatter {finish.name} over the top if using and serve at room temperature.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} üzerine serpin ve oda sıcaklığında servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "acid", "sweet", "fresh", "fat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{grain.name} & {vegetable.name} salatası ve {acid.name}",
      },
    },
  },
  {
    id: "spinach-strawberry-walnut-salad",
    nameTemplate: "{base.name} salad with {crunch.name} & {acid.name}",
    foodType: "salad",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 8,
    totalMinutes: 8,
    slots: [
      slot("base", "Salad greens", true, "spinach", [
        option("spinach", 150, "g", "baby spinach", 3, undefined, {
          tr: { preparation: "bebek ıspanak" },
        }),
        option("cabbage", 200, "g", "very finely shredded", 2, undefined, {
          tr: { preparation: "çok ince doğranmış" },
        }),
      ]),
      slot("crunch", "Crunch element", true, "walnuts", [
        option("walnuts", 40, "g", "roughly chopped", 3, undefined, {
          tr: { preparation: "kabaca doğranmış" },
        }),
        option("pumpkin-seeds", 40, "g", "toasted", 2, undefined, {
          tr: { preparation: "hafifçe kavrulmuş" },
        }),
      ]),
      slot("richness", "Richness", true, "avocado", [
        option("avocado", 1, "piece", "sliced", 3, undefined, {
          tr: { preparation: "dilimlenmiş" },
        }),
        option("feta", 80, "g", "crumbled", 2, undefined, {
          tr: { preparation: "ufalanmış" },
        }),
      ]),
      slot("acid", "Dressing acid", true, "balsamic-vinegar", [
        option("balsamic-vinegar", 1, "tbsp", undefined, 3),
        option("lemon", 1, "piece", "juiced", 2, undefined, {
          tr: { preparation: "suyu çıkarılmış" },
        }),
      ]),
      slot("oil", "Oil", true, "olive-oil", [option("olive-oil", 2, "tbsp")]),
      slot("finish", "Finish", false, "maple-syrup", [
        option("maple-syrup", 1, "tsp", undefined, 3),
        option("sumac", 1, "tsp", undefined, 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "make-dressing",
        template:
          "Whisk together {acid.name}, {oil.name}, {finish.name} if using, {salt.name}, and {black-pepper.name} in a small bowl.",
        translations: {
          tr: {
            template:
              "Küçük bir kasede {acid.name}, {oil.name}, kullanıyorsanız {finish.name}, {salt.name} ve {black-pepper.name} çırpın.",
          },
        },
      },
      {
        id: "assemble",
        template:
          "Place {base.name} in a large bowl. Top with {crunch.name} and {richness.name}.",
        translations: {
          tr: {
            template:
              "{base.name} büyük bir kaseye koyun. Üzerine {crunch.name} ve {richness.name} ekleyin.",
          },
        },
      },
      {
        id: "dress",
        template:
          "Drizzle the dressing over the salad and toss gently to coat. Serve immediately.",
        translations: {
          tr: {
            template:
              "Sosu salatanın üzerine gezdirin ve hafifçe karıştırın. Hemen servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["fresh", "fat", "acid", "crunch", "sweet"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{base.name} salatası ve {crunch.name} & {acid.name}",
      },
    },
  },
  {
    id: "couscous-roasted-eggplant-salad",
    nameTemplate: "{grain.name}, {vegetable.name} & {protein.name} salad",
    foodType: "salad",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 35,
    slots: [
      slot("grain", "Grain base", true, "couscous", [
        option("couscous", 160, "g", "prepared", 3, undefined, {
          tr: { preparation: "hazırlanmış" },
        }),
        option("bulgur", 160, "g", "cooked", 2, undefined, {
          tr: { preparation: "pişirilmiş" },
        }),
      ]),
      slot("vegetable", "Roasted vegetable", true, "eggplant", [
        option("eggplant", 400, "g", "cut into 2 cm cubes", 3, undefined, {
          tr: { preparation: "2 cm'lik küpler halinde doğranmış" },
        }),
        option("zucchini", 400, "g", "cut into half-moons", 2, undefined, {
          tr: { preparation: "yarım ay şeklinde kesilmiş" },
        }),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3, undefined, {
          tr: { preparation: "süzülmüş" },
        }),
        option("feta", 100, "g", "crumbled", 2, undefined, {
          tr: { preparation: "ufalanmış" },
        }),
      ]),
      slot("herb", "Fresh herb", true, "mint", [
        option("mint", 15, "g", "chopped", 3, undefined, {
          tr: { preparation: "doğranmış" },
        }),
        option("parsley", 20, "g", "chopped", 2, undefined, {
          tr: { preparation: "doğranmış" },
        }),
      ]),
      slot("oil", "Oil", true, "olive-oil", [option("olive-oil", 3, "tbsp")]),
      slot("acid", "Dressing acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3, undefined, {
          tr: { preparation: "suyu çıkarılmış" },
        }),
        option("pomegranate-molasses", 2, "tbsp", undefined, 2),
      ]),
      slot("finish", "Finish", false, "pomegranate", [
        option("pomegranate", 60, "g", "seeds", 3, undefined, {
          tr: { preparation: "taneleri" },
        }),
        option("sumac", 1, "tsp", undefined, 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 220°C.",
        translations: { tr: { template: "Fırını 220°C'ye ısıtın." } },
      },
      {
        id: "roast-veg",
        template:
          "Toss {vegetable.name} with {oil.name}, {salt.name}, and {black-pepper.name}. Spread on a baking sheet and roast for 22 to 25 minutes until golden.",
        translations: {
          tr: {
            template:
              "{vegetable.name} üzerine {oil.name}, {salt.name} ve {black-pepper.name} gezdirin. Tepsiye yayın ve altın rengine gelene kadar 22-25 dakika kızartın.",
          },
        },
      },
      {
        id: "prepare-grain",
        template:
          "While the vegetables roast, prepare {grain.name} according to the packet. Fluff with a fork and stir in the remaining {oil.name} and {acid.name}.",
        translations: {
          tr: {
            template:
              "Sebzeler kızarırken, {grain.name} paketteki talimatlara göre hazırlayın. Çatalla havalandırın, kalan {oil.name} ve {acid.name} ekleyin.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Fold {vegetable.name}, {protein.name}, and {herb.name} into the {grain.name}. Taste and adjust seasoning.",
        translations: {
          tr: {
            template:
              "{vegetable.name}, {protein.name} ve {herb.name} {grain.name} ile karıştırın. Tadına bakın ve baharatı ayarlayın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Scatter {finish.name} over the top if using and serve warm or at room temperature.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} üzerine serpin ve sıcak ya da oda sıcaklığında servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "bulk", "fat", "acid", "fresh"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate:
          "{grain.name}, {vegetable.name} & {protein.name} salatası",
      },
    },
  },
  {
    id: "tofu-miso-cabbage-slaw",
    nameTemplate: "Crispy {protein.name} & {base.name} slaw",
    foodType: "salad",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 20,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option(
          "tofu",
          280,
          "g",
          "pressed and sliced into batons",
          3,
          undefined,
          {
            tr: { preparation: "sıkılmış ve çubuk şeklinde kesilmiş" },
          },
        ),
        option("edamame", 150, "g", "shelled", 2, undefined, {
          tr: { preparation: "kabuğu çıkarılmış" },
        }),
      ]),
      slot("base", "Crunchy base", true, "cabbage", [
        option("cabbage", 300, "g", "very finely shredded", 3, undefined, {
          tr: { preparation: "çok ince doğranmış" },
        }),
        option("bok-choy", 300, "g", "thinly sliced", 2, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
      ]),
      slot("veg", "Crisp vegetable", true, "carrot", [
        option("carrot", 200, "g", "julienned", 3, undefined, {
          tr: { preparation: "jülyen kesilmiş" },
        }),
        option("cucumber", 200, "g", "julienned", 2, undefined, {
          tr: { preparation: "jülyen kesilmiş" },
        }),
      ]),
      slot("dressing", "Dressing", true, "miso", [
        option("miso", 2, "tbsp"),
        option("soy-sauce", 2, "tbsp"),
      ]),
      slot("oil", "Oil", true, "sesame-oil", [
        option("sesame-oil", 2, "tbsp"),
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("acid", "Acid", true, "rice-vinegar", [
        option("rice-vinegar", 2, "tbsp"),
        option("lime", 1, "piece", "juiced", 2, undefined, {
          tr: { preparation: "suyu çıkarılmış" },
        }),
      ]),
      slot("finish", "Garnish", false, "sesame-seeds", [
        option("sesame-seeds", 1, "tbsp", undefined, 3),
        option("scallion", 2, "piece", "thinly sliced", 2, undefined, {
          tr: { preparation: "ince dilimlenmiş" },
        }),
      ]),
      pantry.salt,
    ],
    steps: [
      {
        id: "crisp-protein",
        template:
          "Pat {protein.name} dry. Warm a non-stick pan over high heat and sear {protein.name} for 3 to 4 minutes per side until golden and crispy. Season with {salt.name}.",
        translations: {
          tr: {
            template:
              "{protein.name} iyice kurulayın. Yapışmaz tavayı yüksek ateşte ısıtın ve {protein.name} her iki tarafını 3-4 dakika altın rengi ve çıtır olana kadar serin. {salt.name} ekleyin.",
          },
        },
      },
      {
        id: "make-dressing",
        template:
          "Whisk together {dressing.name}, {acid.name}, and {oil.name} in a small bowl.",
        translations: {
          tr: {
            template:
              "Küçük bir kasede {dressing.name}, {acid.name} ve {oil.name} çırpın.",
          },
        },
      },
      {
        id: "toss-slaw",
        template:
          "Combine {base.name} and {veg.name} in a large bowl. Pour over the dressing and toss well.",
        translations: {
          tr: {
            template:
              "{base.name} ve {veg.name} büyük bir kaseye alın. Sosu üzerine dökün ve iyice karıştırın.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Top the slaw with {protein.name} and scatter {finish.name} over the top if using. Serve immediately.",
        translations: {
          tr: {
            template:
              "Slaw'ın üzerine {protein.name} koyun ve kullanıyorsanız {finish.name} serpin. Hemen servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "crunch", "fresh", "umami", "acid"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Çıtır {protein.name} & {base.name} slaw",
      },
    },
  },
  {
    id: "quinoa-cucumber-salad",
    nameTemplate: "{base.name}, {vegetable.name} & herb salad",
    foodType: "salad",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 15,
    slots: [
      slot("base", "Base", true, "quinoa", [
        option("quinoa", 300, "g", "cooked and cooled"),
      ]),
      slot("vegetable", "Fresh vegetable", true, "cucumber", [
        option("cucumber", 300, "g", "diced", 3),
        option("zucchini", 300, "g", "finely diced", 1),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 200, "g", "drained", 3),
        option("lentils", 220, "g", "cooked and drained", 2),
      ]),
      slot("herb", "Fresh herb", true, "parsley", [
        option("parsley", 20, "g", "chopped", 3),
        option("cilantro", 20, "g", "chopped", 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("balsamic-vinegar", 1, "tbsp", undefined, 1),
      ]),
      slot("richness", "Richness", true, "olive-oil", [
        option("olive-oil", 2, "tbsp", undefined, 3),
        option("tahini", 2, "tbsp", undefined, 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "make-dressing",
        template:
          "Whisk {acid.name}, {richness.name}, {salt.name}, and {black-pepper.name} in a large bowl.",
        translations: {
          tr: {
            template:
              "{acid.name}, {richness.name}, {salt.name} ve {black-pepper.name} büyük bir kasede çırpın.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add {base.name}, {vegetable.name}, {protein.name}, and {herb.name}. Toss well.",
        translations: {
          tr: {
            template:
              "{base.name}, {vegetable.name}, {protein.name} ve {herb.name} ekleyin. İyice karıştırın.",
          },
        },
      },
      {
        id: "rest",
        template: "Let the salad stand for 5 minutes, then taste and serve.",
        translations: {
          tr: {
            template:
              "Salatayı 5 dakika dinlendirin, ardından tadına bakıp servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "acid", "fat", "fresh", "crunch"],
    editorialPriority: 10,
    sourceNote: "Original FlavorCraft recipe family, created for the MVP.",
    translations: {
      tr: {
        nameTemplate: "{base.name}, {vegetable.name} ve ot salatası",
      },
    },
  },
  {
    id: "chickpea-tomato-salad",
    nameTemplate: "{protein.name}, {vegetable.name} & {richness.name} salad",
    foodType: "salad",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 12,
    slots: [
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3),
        option("black-beans", 240, "g", "drained", 2),
      ]),
      slot("vegetable", "Fresh vegetable", true, "tomato", [
        option("tomato", 350, "g", "cut into wedges", 3),
        option("bell-pepper", 300, "g", "diced", 1),
      ]),
      slot("richness", "Creamy element", true, "feta", [
        option("feta", 100, "g", "crumbled", 3),
        option("tofu", 120, "g", "crumbled", 2),
        option("tahini", 2, "tbsp", undefined, 1),
      ]),
      slot("herb", "Fresh herb", true, "parsley", [
        option("parsley", 20, "g", "chopped", 3),
        option("cilantro", 20, "g", "chopped", 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("balsamic-vinegar", 1, "tbsp", undefined, 2),
      ]),
      slot("oil", "Dressing fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("heat", "Heat", false, "chili-flakes", [
        option("chili-flakes", 1, "pinch"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "dress",
        template:
          "Whisk {acid.name}, {oil.name}, {salt.name}, {black-pepper.name}, and {heat.name} if using.",
        translations: {
          tr: {
            template:
              "{acid.name}, {oil.name}, {salt.name}, {black-pepper.name} ve kullanıyorsanız {heat.name} çırpın.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add {protein.name}, {vegetable.name}, {richness.name}, and {herb.name}. Fold together gently.",
        translations: {
          tr: {
            template:
              "{protein.name}, {vegetable.name}, {richness.name} ve {herb.name} ekleyin. Hafifçe karıştırın.",
          },
        },
      },
      {
        id: "serve",
        template: "Taste, adjust the acidity, and serve immediately.",
        translations: {
          tr: {
            template: "Tadına bakın, asitliği ayarlayın ve hemen servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "fat", "acid", "fresh", "heat"],
    editorialPriority: 9,
    sourceNote: "Original FlavorCraft recipe family, created for the MVP.",
    translations: {
      tr: {
        nameTemplate:
          "{protein.name}, {vegetable.name} ve {richness.name} salatası",
      },
    },
  },
  {
    id: "lentil-pepper-salad",
    nameTemplate: "{protein.name} & crunchy {vegetable.name} salad",
    foodType: "salad",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 15,
    slots: [
      slot("protein", "Protein", true, "lentils", [
        option("lentils", 300, "g", "cooked and drained", 3),
        option("black-beans", 240, "g", "drained", 1),
      ]),
      slot("vegetable", "Crunchy vegetable", true, "bell-pepper", [
        option("bell-pepper", 300, "g", "diced", 3),
        option("cucumber", 300, "g", "diced", 2),
        option("carrot", 250, "g", "coarsely grated", 1),
      ]),
      slot("green", "Leafy green", true, "spinach", [
        option("spinach", 120, "g", "roughly chopped"),
      ]),
      slot("dressing", "Dressing", true, "yogurt", [
        option("yogurt", 120, "g", undefined, 3),
        option("tahini", 2, "tbsp", undefined, 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("spice", "Spice", false, "cumin", [option("cumin", 1, "tsp")]),
      slot("crunch", "Crunch", false, "pumpkin-seeds", [
        option("pumpkin-seeds", 2, "tbsp", undefined, 3),
        option("bread", 80, "g", "toasted and torn", 1),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "make-dressing",
        template:
          "Mix {dressing.name}, {acid.name}, {salt.name}, {black-pepper.name}, and {spice.name} if using.",
        translations: {
          tr: {
            template:
              "{dressing.name}, {acid.name}, {salt.name}, {black-pepper.name} ve kullanıyorsanız {spice.name} karıştırın.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add {protein.name}, {vegetable.name}, and {green.name}. Toss until evenly coated.",
        translations: {
          tr: {
            template:
              "{protein.name}, {vegetable.name} ve {green.name} ekleyin. Her yer eşit kaplandığında iyice karıştırın.",
          },
        },
      },
      {
        id: "finish",
        template: "Top with {crunch.name} if using and serve.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {crunch.name} üzerine koyun ve servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "acid", "fat", "fresh", "crunch"],
    editorialPriority: 8,
    sourceNote: "Original FlavorCraft recipe family, created for the MVP.",
    translations: {
      tr: {
        nameTemplate: "{protein.name} ve çıtır {vegetable.name} salatası",
      },
    },
  },
  {
    id: "avocado-black-bean-salad",
    nameTemplate: "{protein.name} & {richness.name} salad",
    foodType: "salad",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 12,
    slots: [
      slot("protein", "Protein", true, "black-beans", [
        option("black-beans", 240, "g", "drained", 3),
        option("chickpeas", 240, "g", "drained", 2),
        option("lentils", 220, "g", "cooked and drained", 1),
      ]),
      slot("richness", "Creamy element", true, "avocado", [
        option("avocado", 1, "piece", "diced", 3),
        option("tahini", 2, "tbsp", undefined, 1),
      ]),
      slot("vegetable", "Fresh vegetable", true, "tomato", [
        option("tomato", 300, "g", "diced", 3),
        option("bell-pepper", 280, "g", "diced", 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("herb", "Fresh herb", true, "cilantro", [
        option("cilantro", 20, "g", "chopped", 3),
        option("parsley", 20, "g", "chopped", 2),
      ]),
      slot("oil", "Dressing fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("heat", "Heat", false, "chili-flakes", [
        option("chili-flakes", 1, "pinch"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "make-dressing",
        template:
          "Whisk {acid.name}, {oil.name}, {salt.name}, {black-pepper.name}, and {heat.name} if using.",
        translations: {
          tr: {
            template:
              "{acid.name}, {oil.name}, {salt.name}, {black-pepper.name} ve kullanıyorsanız {heat.name} çırpın.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Dice {richness.name} and fold it gently with {protein.name}, {vegetable.name}, and {herb.name}.",
        translations: {
          tr: {
            template:
              "{richness.name} küp küp doğrayın ve {protein.name}, {vegetable.name} ve {herb.name} ile hafifçe karıştırın.",
          },
        },
      },
      {
        id: "dress",
        template:
          "Pour the dressing over the salad, fold once more, and serve immediately.",
        translations: {
          tr: {
            template:
              "Sosu salatanın üzerine dökün, bir kez daha karıştırın ve hemen servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "fat", "acid", "fresh", "heat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{protein.name} ve {richness.name} salatası",
      },
    },
  },
  {
    id: "carrot-cabbage-slaw",
    nameTemplate: "Crunchy {vegetable.name} & {green.name} slaw",
    foodType: "salad",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 12,
    slots: [
      slot("vegetable", "Crunchy vegetable", true, "carrot", [
        option("carrot", 250, "g", "coarsely grated", 3),
        option("bell-pepper", 280, "g", "finely sliced", 2),
      ]),
      slot("green", "Crunchy green", true, "cabbage", [
        option("cabbage", 250, "g", "finely shredded"),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 200, "g", "drained", 3),
        option("lentils", 220, "g", "cooked and drained", 2),
        option("black-beans", 200, "g", "drained", 1),
      ]),
      slot("dressing", "Dressing", true, "tahini", [
        option("tahini", 2, "tbsp", undefined, 3),
        option("olive-oil", 2, "tbsp", undefined, 1),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("herb", "Fresh herb", false, "cilantro", [
        option("cilantro", 20, "g", "chopped", 3),
        option("parsley", 20, "g", "chopped", 2),
      ]),
      slot("crunch", "Crunch", false, "pumpkin-seeds", [
        option("pumpkin-seeds", 2, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "make-dressing",
        template:
          "Whisk {dressing.name}, {acid.name}, {salt.name}, and {black-pepper.name} until smooth.",
        translations: {
          tr: {
            template:
              "{dressing.name}, {acid.name}, {salt.name} ve {black-pepper.name} pürüzsüz olana kadar çırpın.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add {vegetable.name}, {green.name}, {protein.name}, and {herb.name} if using. Toss until evenly coated.",
        translations: {
          tr: {
            template:
              "{vegetable.name}, {green.name}, {protein.name} ve kullanıyorsanız {herb.name} ekleyin. Her şey eşit kaplandığında iyice karıştırın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Let the slaw rest for 5 minutes. Top with {crunch.name} if using and serve.",
        translations: {
          tr: {
            template:
              "Kolesloyu 5 dakika dinlendirin. Kullanıyorsanız {crunch.name} üzerine koyun ve servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "fat", "acid", "fresh", "crunch"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Çıtır {vegetable.name} ve {green.name} koleslosu",
      },
    },
  },
  {
    id: "egg-cucumber-salad",
    nameTemplate: "{protein.name} & {vegetable.name} lunch salad",
    foodType: "salad",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 10,
    slots: [
      slot("protein", "Protein", true, "egg", [
        option("egg", 4, "piece", "hard-boiled and quartered", 3, {
          safetyCue:
            "Use fully hard-boiled eggs, with both whites and yolks firm, and quarter them.",
        }),
        option("tofu", 300, "g", "pressed and cubed", 2, {
          safetyCue: "Press and cube the tofu.",
        }),
        option("chickpeas", 240, "g", "drained", 1, {
          safetyCue: "Drain the chickpeas.",
        }),
      ]),
      slot("vegetable", "Fresh vegetable", true, "cucumber", [
        option("cucumber", 300, "g", "sliced", 3),
        option("tomato", 300, "g", "cut into wedges", 2),
      ]),
      slot("green", "Green", true, "spinach", [option("spinach", 100, "g")]),
      slot("dressing", "Dressing", true, "yogurt", [
        option("yogurt", 120, "g", undefined, 3),
        option("tahini", 2, "tbsp", undefined, 2),
        option("olive-oil", 2, "tbsp", undefined, 1),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("herb", "Herb", false, "cilantro", [
        option("cilantro", 15, "g", "chopped", 3),
        option("parsley", 15, "g", "chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "prepare-protein",
        template: "{protein.safetyCue}",
        translations: {
          tr: {
            template: "{protein.safetyCue}",
          },
        },
      },
      {
        id: "make-dressing",
        template:
          "Whisk {dressing.name}, {acid.name}, {salt.name}, and {black-pepper.name}.",
        translations: {
          tr: {
            template:
              "{dressing.name}, {acid.name}, {salt.name} ve {black-pepper.name} çırpın.",
          },
        },
      },
      {
        id: "build-salad",
        template:
          "Arrange {green.name}, {vegetable.name}, and {protein.name} on a platter.",
        translations: {
          tr: {
            template:
              "{green.name}, {vegetable.name} ve {protein.name} bir servis tabağına dizin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Spoon over the dressing and finish with {herb.name} if using.",
        translations: {
          tr: {
            template:
              "Sosu üzerine kaşıkla dökün ve kullanıyorsanız {herb.name} ile tamamlayın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "fat", "acid", "fresh"],
    editorialPriority: 7,
    sourceNote: "Original FlavorCraft recipe family, created for the MVP.",
    translations: {
      tr: {
        nameTemplate: "{protein.name} ve {vegetable.name} öğle salatası",
      },
    },
  },
  {
    id: "warm-lentil-salad",
    nameTemplate: "Warm {protein.name} & {green.name} salad",
    foodType: "salad",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 20,
    totalMinutes: 25,
    slots: [
      slot("protein", "Protein", true, "lentils", [
        option("lentils", 200, "g", "cooked and drained", 3),
        option("chickpeas", 240, "g", "drained", 2),
      ]),
      slot("green", "Leafy green", true, "spinach", [
        option("spinach", 120, "g", "roughly chopped", 3),
        option("cabbage", 200, "g", "finely shredded", 2),
      ]),
      slot("vegetable", "Warm vegetable", true, "mushroom", [
        option("mushroom", 250, "g", "thinly sliced", 3),
        option("tomato", 250, "g", "roughly chopped", 2),
        option("bell-pepper", 250, "g", "thinly sliced", 1),
      ]),
      slot("acid", "Dressing acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("balsamic-vinegar", 2, "tbsp", undefined, 2),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("spice", "Spice", false, "smoked-paprika", [
        option("smoked-paprika", 1, "tsp", undefined, 3),
        option("cumin", 1, "tsp", undefined, 2),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "chopped", 3),
        option("cilantro", 15, "g", "chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "warm-base",
        template:
          "Warm {oil.name} in a wide pan over medium heat. Add {garlic.name} and cook for 1 minute.",
        translations: {
          tr: {
            template:
              "Geniş bir tavada orta ateşte {oil.name} ısıtın. {garlic.name} ekleyip 1 dakika pişirin.",
          },
        },
      },
      {
        id: "cook-vegetable",
        template:
          "Add {vegetable.name} and {spice.name} if using. Cook for 5 to 6 minutes until softened.",
        translations: {
          tr: {
            template:
              "{vegetable.name} ve kullanıyorsanız {spice.name} ekleyin. Yumuşayana kadar 5-6 dakika pişirin.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add {protein.name} and {green.name}. Toss until the greens wilt, about 2 minutes.",
        translations: {
          tr: {
            template:
              "{protein.name} ve {green.name} ekleyin. Yapraklar solana kadar yaklaşık 2 dakika karıştırın.",
          },
        },
      },
      {
        id: "dress",
        template:
          "Remove from heat. Add {acid.name}, {salt.name}, and {black-pepper.name}. Toss well and scatter {herb.name} over the top if using.",
        translations: {
          tr: {
            template:
              "Ocaktan alın. {acid.name}, {salt.name} ve {black-pepper.name} ekleyin. İyice karıştırın ve kullanıyorsanız {herb.name} üzerine serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "fresh", "acid", "fat", "umami"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Sıcak {protein.name} ve {green.name} salatası",
      },
    },
  },
  {
    id: "pan-wilted-spinach-tofu-salad",
    nameTemplate: "Pan-seared {protein.name} & wilted {green.name} salad",
    foodType: "salad",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 16,
    totalMinutes: 18,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 280, "g", "pressed and cubed", 3),
        option("chickpeas", 240, "g", "drained and dried", 2),
      ]),
      slot("green", "Leafy green", true, "spinach", [
        option("spinach", 180, "g", "roughly chopped", 3),
        option("cabbage", 250, "g", "finely shredded", 2),
      ]),
      slot("vegetable", "Fresh vegetable", true, "tomato", [
        option("tomato", 250, "g", "diced", 3),
        option("bell-pepper", 250, "g", "finely sliced", 2),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("sauce", "Umami sauce", true, "soy-sauce", [
        option("soy-sauce", 1, "tbsp", undefined, 3),
        option("miso", 1, "tbsp", undefined, 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("crunch", "Crunch", false, "pumpkin-seeds", [
        option("pumpkin-seeds", 2, "tbsp"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "sear-protein",
        template:
          "Heat {oil.name} in a wide pan over medium-high heat. Add {protein.name} and cook without stirring for 4 minutes until golden, then toss for 1 minute more.",
        translations: {
          tr: {
            template:
              "Geniş bir tavada orta-yüksek ateşte {oil.name} ısıtın. {protein.name} ekleyip karıştırmadan 4 dakika altın rengi alana kadar pişirin, ardından 1 dakika daha karıştırın.",
          },
        },
      },
      {
        id: "add-aromatics",
        template:
          "Add {garlic.name} and {vegetable.name}. Cook for 2 to 3 minutes until the {vegetable.name} softens.",
        translations: {
          tr: {
            template:
              "{garlic.name} ve {vegetable.name} ekleyin. {vegetable.name} yumuşayana kadar 2-3 dakika pişirin.",
          },
        },
      },
      {
        id: "wilt-greens",
        template:
          "Add {green.name} and toss for 1 to 2 minutes until just wilted.",
        translations: {
          tr: {
            template:
              "{green.name} ekleyin ve hafifçe solana kadar 1-2 dakika karıştırın.",
          },
        },
      },
      {
        id: "dress",
        template:
          "Remove from heat. Add {sauce.name}, {acid.name}, {salt.name}, and {black-pepper.name}. Toss well, divide between plates, and top with {crunch.name} if using.",
        translations: {
          tr: {
            template:
              "Ocaktan alın. {sauce.name}, {acid.name}, {salt.name} ve {black-pepper.name} ekleyin. İyice karıştırın, tabaklara paylaştırın ve kullanıyorsanız {crunch.name} ile süsleyin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "umami", "acid", "fat", "fresh"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate:
          "Tavada kavrulan {protein.name} ve solmuş {green.name} salatası",
      },
    },
  },
  {
    id: "roasted-root-salad",
    nameTemplate: "Roasted {root.name} & {protein.name} salad",
    foodType: "salad",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 40,
    slots: [
      slot("root", "Root vegetable", true, "sweet-potato", [
        option("sweet-potato", 400, "g", "cut into 2 cm cubes", 3),
        option("carrot", 400, "g", "cut into 3 cm pieces", 2),
        option("potato", 400, "g", "cut into 2 cm cubes", 1),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained and dried", 3),
        option("lentils", 200, "g", "cooked and drained", 2),
      ]),
      slot("green", "Salad base", true, "spinach", [
        option("spinach", 80, "g", "roughly chopped", 3),
        option("cabbage", 150, "g", "finely shredded", 2),
      ]),
      slot("acid", "Dressing acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("balsamic-vinegar", 2, "tbsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("spice", "Warm spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      slot("finish", "Finish", false, "pumpkin-seeds", [
        option("pumpkin-seeds", 3, "tbsp", undefined, 3),
        option("walnuts", 50, "g", "roughly chopped", 2),
        option("avocado", 1, "piece", "sliced", 1),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "chopped", 3),
        option("cilantro", 15, "g", "chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 220°C.",
        translations: {
          tr: { template: "Fırını 220°C'ye ısıtın." },
        },
      },
      {
        id: "roast",
        template:
          "Toss {root.name} and {protein.name} with half the {oil.name}, {spice.name}, {salt.name}, and {black-pepper.name} on a large baking tray. Roast for 25 to 30 minutes, turning once, until golden.",
        translations: {
          tr: {
            template:
              "{root.name} ve {protein.name} büyük bir fırın tepsisinde {oil.name} yarısı, {spice.name}, {salt.name} ve {black-pepper.name} ile karıştırın. Bir kez çevirerek altın rengi alana kadar 25-30 dakika kızartın.",
          },
        },
      },
      {
        id: "dress",
        template:
          "Whisk the remaining {oil.name} with {acid.name} and toss with {green.name}.",
        translations: {
          tr: {
            template:
              "Kalan {oil.name} ile {acid.name} çırpın ve {green.name} ile karıştırın.",
          },
        },
      },
      {
        id: "assemble",
        template:
          "Spoon the roasted {root.name} and {protein.name} over the dressed {green.name}. Top with {finish.name} and {herb.name} if using.",
        translations: {
          tr: {
            template:
              "Fırınlanmış {root.name} ve {protein.name} soslu {green.name} üzerine kaşıkla koyun. Kullanıyorsanız {finish.name} ve {herb.name} ile süsleyin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fresh", "acid", "fat", "aroma"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Fırınlanmış {root.name} ve {protein.name} salatası",
      },
    },
  },
  {
    id: "roasted-cauliflower-lentil-salad",
    nameTemplate: "Roasted {vegetable.name} & {protein.name} salad",
    foodType: "salad",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 38,
    slots: [
      slot("vegetable", "Roasting vegetable", true, "cauliflower", [
        option("cauliflower", 400, "g", "cut into small florets", 3),
        option("broccoli", 400, "g", "cut into florets", 2),
        option("bell-pepper", 350, "g", "cut into thick strips", 1),
      ]),
      slot("protein", "Protein", true, "lentils", [
        option("lentils", 200, "g", "cooked and drained", 3),
        option("chickpeas", 240, "g", "drained and dried", 2),
      ]),
      slot("green", "Salad base", true, "spinach", [
        option("spinach", 80, "g", "roughly chopped", 3),
        option("cabbage", 150, "g", "finely shredded", 2),
      ]),
      slot("dressing", "Creamy dressing", true, "tahini", [
        option("tahini", 2, "tbsp", undefined, 3),
        option("peanut-butter", 2, "tbsp", undefined, 2),
      ]),
      slot("acid", "Dressing acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("oil", "Roasting fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("spice", "Warm spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      slot("crunch", "Crunch", false, "pumpkin-seeds", [
        option("pumpkin-seeds", 3, "tbsp"),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "chopped", 3),
        option("cilantro", 15, "g", "chopped", 2),
      ]),
      pantry.water(30),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 220°C.",
        translations: {
          tr: { template: "Fırını 220°C'ye ısıtın." },
        },
      },
      {
        id: "roast",
        template:
          "Toss {vegetable.name} and {protein.name} with {oil.name}, {spice.name}, {salt.name}, and {black-pepper.name} on a large baking tray. Roast for 25 minutes, turning once, until golden at the edges.",
        translations: {
          tr: {
            template:
              "{vegetable.name} ve {protein.name} büyük bir fırın tepsisinde {oil.name}, {spice.name}, {salt.name} ve {black-pepper.name} ile karıştırın. Bir kez çevirerek kenarları altın rengi alana kadar 25 dakika kızartın.",
          },
        },
      },
      {
        id: "make-dressing",
        template:
          "Whisk {dressing.name}, {acid.name}, and {water.name} until smooth and pourable. Season with {salt.name}.",
        translations: {
          tr: {
            template:
              "{dressing.name}, {acid.name} ve {water.name} pürüzsüz ve akıcı kıvama gelene kadar çırpın. {salt.name} ile tatlandırın.",
          },
        },
      },
      {
        id: "assemble",
        template:
          "Spread {green.name} on serving plates. Spoon over the roasted mixture, drizzle with the dressing, and top with {crunch.name} and {herb.name} if using.",
        translations: {
          tr: {
            template:
              "Servis tabağına {green.name} yayın. Üzerine fırınlanmış karışımı kaşıkla koyun, sosu gezdirin ve kullanıyorsanız {crunch.name} ve {herb.name} ile süsleyin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "fat", "acid", "bitter", "fresh"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Fırınlanmış {vegetable.name} ve {protein.name} salatası",
      },
    },
  },
  {
    id: "roasted-tomato-tofu-salad",
    nameTemplate: "Roasted {vegetable.name} & {protein.name} salad",
    foodType: "salad",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 35,
    slots: [
      slot("vegetable", "Roasting vegetable", true, "tomato", [
        option("tomato", 350, "g", "halved", 3),
        option("bell-pepper", 350, "g", "cut into thick strips", 1),
      ]),
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 300, "g", "pressed and cubed", 3),
        option("chickpeas", 240, "g", "drained and dried", 2),
      ]),
      slot("green", "Salad base", true, "spinach", [
        option("spinach", 80, "g", "roughly chopped", 3),
        option("cabbage", 150, "g", "finely shredded", 2),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("oil", "Roasting fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("acid", "Dressing acid", true, "balsamic-vinegar", [
        option("balsamic-vinegar", 2, "tbsp", undefined, 3),
        option("lemon", 1, "piece", "juiced", 2),
      ]),
      slot("crunch", "Crunch", false, "pumpkin-seeds", [
        option("pumpkin-seeds", 2, "tbsp"),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("cilantro", 15, "g", "roughly chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 220°C.",
        translations: {
          tr: { template: "Fırını 220°C'ye ısıtın." },
        },
      },
      {
        id: "roast",
        template:
          "Toss {vegetable.name}, {protein.name}, and {garlic.name} with {oil.name}, {salt.name}, and {black-pepper.name} on a large baking tray. Roast for 22 minutes until golden at the edges.",
        translations: {
          tr: {
            template:
              "{vegetable.name}, {protein.name} ve {garlic.name} büyük bir fırın tepsisinde {oil.name}, {salt.name} ve {black-pepper.name} ile karıştırın. Kenarları altın rengi alana kadar 22 dakika kızartın.",
          },
        },
      },
      {
        id: "dress",
        template:
          "Drizzle {acid.name} over the warm roasted mixture and toss gently.",
        translations: {
          tr: {
            template:
              "Sıcak fırınlanmış karışımın üzerine {acid.name} gezdirin ve hafifçe karıştırın.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Spread {green.name} on plates. Spoon over the roasted mixture and scatter {crunch.name} and {herb.name} if using.",
        translations: {
          tr: {
            template:
              "Tabaklara {green.name} yayın. Üzerine fırınlanmış karışımı kaşıkla koyun ve kullanıyorsanız {crunch.name} ve {herb.name} serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "fat", "acid", "umami", "fresh"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "Fırınlanmış {vegetable.name} ve {protein.name} salatası",
      },
    },
  },
  {
    id: "tofu-tomato-onion-salad",
    nameTemplate: "{protein.name}, {vegetable.name} & {onion.name} salad",
    foodType: "salad",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 12,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 300, "g", "pressed and cubed", 3),
        option("chickpeas", 240, "g", "drained", 2),
      ]),
      slot("vegetable", "Fresh vegetable", true, "tomato", [
        option("tomato", 300, "g", "diced", 3),
        option("bell-pepper", 280, "g", "diced", 2),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "thinly sliced"),
      ]),
      slot("green", "Leafy green", true, "spinach", [
        option("spinach", 80, "g", "roughly chopped", 3),
        option("cabbage", 120, "g", "finely shredded", 2),
      ]),
      slot("dressing", "Dressing", true, "tahini", [
        option("tahini", 2, "tbsp", undefined, 3),
        option("olive-oil", 2, "tbsp", undefined, 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "chopped", 3),
        option("cilantro", 15, "g", "chopped", 2),
      ]),
      slot("heat", "Heat", false, "chili-flakes", [
        option("chili-flakes", 1, "pinch"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "make-dressing",
        template:
          "Whisk {dressing.name}, {acid.name}, {salt.name}, {black-pepper.name}, and {heat.name} if using until smooth.",
        translations: {
          tr: {
            template:
              "{dressing.name}, {acid.name}, {salt.name}, {black-pepper.name} ve kullanıyorsanız {heat.name} pürüzsüz olana kadar çırpın.",
          },
        },
      },
      {
        id: "combine",
        template:
          "Add {protein.name}, {vegetable.name}, {onion.name}, and {green.name}. Toss until evenly dressed.",
        translations: {
          tr: {
            template:
              "{protein.name}, {vegetable.name}, {onion.name} ve {green.name} ekleyin. Her şey eşit sosulandığında iyice karıştırın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Taste and adjust seasoning. Scatter {herb.name} if using and serve immediately.",
        translations: {
          tr: {
            template:
              "Tadına bakın ve baharatı ayarlayın. Kullanıyorsanız {herb.name} serpin ve hemen servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "fat", "acid", "fresh", "heat"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate:
          "{protein.name}, {vegetable.name} ve {onion.name} salatası",
      },
    },
  },
  {
    id: "pomegranate-pistachio-salad",
    nameTemplate: "{greens.name} salad with {crunch.name} & {fresh.name}",
    foodType: "salad",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 10,
    totalMinutes: 10,
    slots: [
      slot("greens", "Salad base", true, "spinach", [
        option("spinach", 100, "g", undefined, 3),
        option("cabbage", 200, "g", "very finely shredded", 2),
      ]),
      slot("crunch", "Crunchy topping", true, "pistachios", [
        option("pistachios", 40, "g", "roughly chopped", 3),
        option("walnuts", 40, "g", "roughly chopped", 2),
      ]),
      slot("fresh", "Fresh fruit", true, "pomegranate", [
        option("pomegranate", 100, "g", "seeds only", 3),
        option("tomato", 200, "g", "finely diced", 2),
      ]),
      slot("onion", "Sharp element", true, "red-onion", [
        option("red-onion", 1, "piece", "very finely sliced", 3),
        option("scallion", 3, "piece", "thinly sliced", 2),
      ]),
      slot("spice", "Spice dressing", true, "sumac", [
        option("sumac", 1.5, "tsp", undefined, 3),
        option("smoked-paprika", 0.5, "tsp", undefined, 2),
      ]),
      slot("dressing", "Dressing sweetness", true, "pomegranate-molasses", [
        option("pomegranate-molasses", 1.5, "tbsp", undefined, 3),
        option("balsamic-vinegar", 1, "tbsp", undefined, 2),
      ]),
      slot("herb", "Fresh herb", false, "dill", [
        option("dill", 15, "g", "roughly torn", 3),
        option("parsley", 15, "g", "roughly chopped", 2),
      ]),
      slot("oil", "Oil", true, "olive-oil", [option("olive-oil", 3, "tbsp")]),
      pantry.salt,
    ],
    steps: [
      {
        id: "make-dressing",
        template:
          "Whisk {oil.name}, {dressing.name}, {spice.name}, and {salt.name} together in a large bowl.",
        translations: {
          tr: {
            template:
              "{oil.name}, {dressing.name}, {spice.name} ve {salt.name} büyük bir kasede birlikte çırpın.",
          },
        },
      },
      {
        id: "build-salad",
        template:
          "Add {greens.name}, {onion.name}, and {herb.name} if using to the dressing; toss to coat.",
        translations: {
          tr: {
            template:
              "Sosin içine {greens.name}, {onion.name} ve kullanıyorsanız {herb.name} ekleyin; iyice karıştırın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Divide between plates and top with {fresh.name} and {crunch.name}. Serve immediately.",
        translations: {
          tr: {
            template:
              "Tabaklara paylaştırın ve üzerine {fresh.name} ve {crunch.name} koyun. Hemen servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["fresh", "crunch", "acid", "fat", "sweet", "aroma"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate:
          "{crunch.name} ve {fresh.name} ile {greens.name} salatası",
      },
    },
  },
  {
    id: "farro-fennel-salad",
    nameTemplate: "{grain.name} & {vegetable.name} salad",
    foodType: "salad",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 35,
    slots: [
      slot("grain", "Grain", true, "farro", [
        option("farro", 180, "g", "cooked and cooled", 3),
        option("barley", 180, "g", "cooked and cooled", 2),
        option("bulgur", 180, "g", "cooked and cooled", 1),
      ]),
      slot("vegetable", "Vegetable", true, "fennel", [
        option("fennel", 1, "piece", "thinly sliced", 3),
        option("cucumber", 1, "piece", "diced", 2),
        option("celery", 2, "piece", "thinly sliced", 1),
      ]),
      slot("legume", "Legume", false, "white-beans", [
        option("white-beans", 200, "g", "drained and rinsed", 3),
        option("chickpeas", 200, "g", "drained and rinsed", 2),
      ]),
      slot("acid", "Dressing acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("red-wine-vinegar", 1, "tbsp", undefined, 2),
      ]),
      slot("herb", "Herb", true, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("dill", 10, "g", "roughly chopped", 2),
        option("basil", 10, "g", "torn", 1),
      ]),
      slot("oil", "Oil", true, "olive-oil", [option("olive-oil", 2, "tbsp")]),
      slot("finish", "Finish", false, "pistachios", [
        option("pistachios", 30, "g", "roughly chopped", 3),
        option("walnuts", 30, "g", "roughly chopped", 2),
        option("pumpkin-seeds", 2, "tbsp", undefined, 1),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "make-dressing",
        template:
          "Whisk {acid.name} and {oil.name} with a pinch of {salt.name} and {black-pepper.name}.",
        translations: {
          tr: {
            template:
              "{acid.name} ve {oil.name} bir tutam {salt.name} ve {black-pepper.name} ile çırpın.",
          },
        },
      },
      {
        id: "build-salad",
        template:
          "Combine {grain.name}, {vegetable.name}, and {legume.name} if using in a large bowl.",
        translations: {
          tr: {
            template:
              "Büyük bir kasede {grain.name}, {vegetable.name} ve kullanıyorsanız {legume.name} karıştırın.",
          },
        },
      },
      {
        id: "dress",
        template:
          "Pour dressing over the salad and toss well. Stir through {herb.name}.",
        translations: {
          tr: {
            template:
              "Sosu salatanın üzerine döküp iyice karıştırın. {herb.name} ekleyip karıştırın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Scatter {finish.name} over the top if using, taste for seasoning, and serve.",
        translations: {
          tr: {
            template:
              "Kullanıyorsanız {finish.name} serpin, baharatı tadın ve servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "acid", "fat", "fresh", "crunch"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{grain.name} ve {vegetable.name} salatası",
      },
    },
  },
  {
    id: "larb-style-salad",
    nameTemplate: "Warm {protein.name} larb with {herb-1.name}",
    foodType: "salad",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 20,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 280, "g", "finely crumbled", 3),
        option("tempeh", 200, "g", "finely crumbled", 2),
        option("chickpeas", 400, "g", "drained and roughly crushed", 1),
      ]),
      slot("herb-1", "Main herb", true, "mint", [
        option("mint", 20, "g", "leaves picked", 3),
        option("cilantro", 20, "g", "roughly chopped", 2),
      ]),
      slot("herb-2", "Second herb", true, "cilantro", [
        option("cilantro", 15, "g", "roughly chopped", 3),
        option("parsley", 15, "g", "roughly chopped", 2),
      ]),
      slot("shallot", "Shallot", true, "red-onion", [
        option("red-onion", 1, "piece", "thinly sliced", 3),
        option("scallion", 3, "piece", "thinly sliced", 2),
      ]),
      slot("acid", "Acid", true, "lime", [
        option("lime", 2, "piece", "juiced", 3),
        option("lemon", 2, "piece", "juiced", 2),
      ]),
      slot("sauce", "Savory sauce", true, "soy-sauce", [
        option("soy-sauce", 1, "tbsp"),
      ]),
      slot("heat", "Heat", false, "chili-flakes", [
        option("chili-flakes", 1, "tsp", undefined, 3),
        option("sriracha", 1, "tsp", undefined, 2),
      ]),
      slot("crunch", "Crunch", false, "sesame-seeds", [
        option("sesame-seeds", 1, "tbsp", "lightly toasted", 3),
        option("cashews", 25, "g", "roughly chopped", 2),
      ]),
      slot("oil", "Cooking fat", true, "sesame-oil", [
        option("sesame-oil", 1, "tbsp"),
        option("olive-oil", 1, "tbsp"),
      ]),
    ],
    steps: [
      {
        id: "cook-protein",
        template:
          "Heat {oil.name} in a large frying pan over medium-high heat. Add {protein.name} and cook, pressing and stirring, for 5–7 minutes until golden in places. Season with {sauce.name} and cook 1 minute more. Remove from heat.",
        translations: {
          tr: {
            template:
              "Büyük bir tavada {oil.name} orta-yüksek ateşte ısıtın. {protein.name} ekleyin ve bastırarak ve karıştırarak 5–7 dakika altın rengi alana kadar pişirin. {sauce.name} ile tatlandırın ve 1 dakika daha pişirin. Ocaktan alın.",
          },
        },
      },
      {
        id: "toss",
        template:
          "While still hot, add {acid.name}, {heat.name} if using, and {shallot.name} to the pan. Toss everything together — the residual heat will soften the shallot slightly.",
        translations: {
          tr: {
            template:
              "Hâlâ sıcakken tavaya {acid.name}, kullanıyorsanız {heat.name} ve {shallot.name} ekleyin. Her şeyi birlikte karıştırın — kalan ısı soğanı hafifçe yumuşatacaktır.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Fold through {herb-1.name} and {herb-2.name}. Pile onto a plate and scatter {crunch.name} if using. Serve immediately at room temperature with steamed rice or lettuce cups.",
        translations: {
          tr: {
            template:
              "{herb-1.name} ve {herb-2.name} katlayarak ekleyin. Bir tabağa yığın ve kullanıyorsanız {crunch.name} serpin. Oda sıcaklığında buharda pişirilmiş pirinç veya marul yapraklarıyla hemen servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "acid", "fresh", "heat", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{herb-1.name} ile sıcak {protein.name} larbı",
      },
    },
  },
  {
    id: "tabbouleh",
    nameTemplate: "Tabbouleh with {herb.name} & {grain.name}",
    foodType: "salad",
    primaryCookingLocation: "no-cook",
    servings: 3,
    activeMinutes: 20,
    totalMinutes: 35,
    slots: [
      slot("herb", "Herb (dominant)", true, "parsley", [
        option("parsley", 200, "g", "very finely chopped", 3),
        option("mint", 80, "g", "very finely chopped", 2),
      ]),
      slot("grain", "Grain accent", true, "bulgur", [
        option(
          "bulgur",
          70,
          "g",
          "fine-grain, soaked in boiling water 15 min then drained",
          3,
        ),
        option("couscous", 70, "g", "prepared and cooled", 2),
        option("quinoa", 70, "g", "cooked and cooled", 1),
      ]),
      slot("tomato", "Tomato", true, "tomato", [
        option("tomato", 2, "piece", "finely diced, excess juice discarded"),
      ]),
      slot("scallion", "Allium", true, "scallion", [
        option("scallion", 4, "piece", "thinly sliced", 3),
        option("onion", 0.5, "piece", "very finely minced and rinsed", 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 2, "piece", "juiced", 3),
        option("lime", 2, "piece", "juiced", 2),
      ]),
      slot("oil", "Fat", true, "olive-oil", [option("olive-oil", 3, "tbsp")]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "soak-grain",
        template:
          "Cover {grain.name} with just-boiled water (enough to submerge by 1 cm), let soak 15 minutes until tender, then drain well and spread to cool completely.",
        translations: {
          tr: {
            template:
              "{grain.name} üzerini 1 cm geçecek kadar kaynar suyla örtün, 15 dakika yumuşayana kadar bekletin, sonra iyice süzüp tamamen soğuması için yayın.",
          },
        },
      },
      {
        id: "chop-herb",
        template:
          "Very finely chop {herb.name} — the herbs are the bulk of this salad, not a garnish. Aim for a fine, almost minced texture rather than rough pieces.",
        translations: {
          tr: {
            template:
              "{herb.name} çok ince doğrayın — otlar bu salatanın ana bileşenidir, garnitür değil. Kaba parçalar değil, ince, neredeyse kıyılmış bir kıvam hedefleyin.",
          },
        },
      },
      {
        id: "combine",
        template:
          "In a large bowl combine {herb.name}, {grain.name}, {tomato.name}, and {scallion.name}.",
        translations: {
          tr: {
            template:
              "Büyük bir kasede {herb.name}, {grain.name}, {tomato.name} ve {scallion.name} karıştırın.",
          },
        },
      },
      {
        id: "dress",
        template:
          "Squeeze {acid.name} over the salad, add {oil.name}, season with {salt.name} and {black-pepper.name}, and toss well. The salad should taste very bright and lemony.",
        translations: {
          tr: {
            template:
              "Salatanın üzerine {acid.name} sıkın, {oil.name} ekleyin, {salt.name} ve {black-pepper.name} ile tatlandırın ve iyice karıştırın. Salata çok parlak ve limonlu tatmalıdır.",
          },
        },
      },
      {
        id: "rest",
        template:
          "Let the tabbouleh rest 10–15 minutes before serving so the herbs soften slightly and absorb the dressing.",
        translations: {
          tr: {
            template:
              "Tabulenin servis edilmeden önce 10–15 dakika dinlenmesine izin verin, böylece otlar hafifçe yumuşar ve sosu emer.",
          },
        },
      },
    ],
    targetBalanceRoles: ["fresh", "acid", "starch", "fat", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{herb.name} ve {grain.name} ile tabule",
      },
    },
  },
  {
    id: "fattoush",
    nameTemplate: "Fattoush with {crouton.name} & {spice.name}",
    foodType: "salad",
    primaryCookingLocation: "oven",
    servings: 3,
    activeMinutes: 20,
    totalMinutes: 30,
    slots: [
      slot("crouton", "Flatbread crouton", true, "flatbread", [
        option("flatbread", 2, "piece", "torn into rough 3 cm pieces"),
        option("bread", 120, "g", "torn into rough chunks", 2),
      ]),
      slot("spice", "Defining spice", true, "sumac", [
        option("sumac", 1, "tbsp"),
      ]),
      slot("tomato", "Tomato", true, "tomato", [
        option("tomato", 2, "piece", "roughly chopped"),
      ]),
      slot("cucumber", "Cucumber", true, "cucumber", [
        option("cucumber", 200, "g", "roughly chopped, seeds scooped out"),
      ]),
      slot("herb", "Fresh herb", true, "parsley", [
        option("parsley", 25, "g", "roughly chopped", 3),
        option("mint", 20, "g", "leaves picked", 2),
      ]),
      slot("allium", "Allium", true, "red-onion", [
        option("red-onion", 0.5, "piece", "very thinly sliced", 3),
        option("scallion", 3, "piece", "thinly sliced", 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 2, "piece", "juiced", 3),
        option("red-wine-vinegar", 1, "tbsp", undefined, 2),
      ]),
      slot("oil", "Fat", true, "olive-oil", [option("olive-oil", 3, "tbsp")]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "toast-croutons",
        template:
          "Preheat oven to 200°C. Toss {crouton.name} pieces with 1 tbsp {oil.name}, spread on a baking tray, and bake 8–10 minutes until golden and crisp. Remove and cool completely.",
        translations: {
          tr: {
            template:
              "Fırını 200°C'ye ısıtın. {crouton.name} parçalarını 1 yemek kaşığı {oil.name} ile karıştırın, bir fırın tepsisine yayın ve altın rengi ve çıtır olana kadar 8–10 dakika pişirin. Çıkarın ve tamamen soğutun.",
          },
        },
      },
      {
        id: "prep-salad",
        template:
          "In a large bowl combine {tomato.name}, {cucumber.name}, {herb.name}, and {allium.name}.",
        translations: {
          tr: {
            template:
              "Büyük bir kasede {tomato.name}, {cucumber.name}, {herb.name} ve {allium.name} karıştırın.",
          },
        },
      },
      {
        id: "dress",
        template:
          "Squeeze {acid.name} over the salad, add remaining {oil.name} and half the {spice.name}, season with {salt.name} and {black-pepper.name}, and toss well.",
        translations: {
          tr: {
            template:
              "Salatanın üzerine {acid.name} sıkın, kalan {oil.name} ve {spice.name} yarısını ekleyin, {salt.name} ve {black-pepper.name} ile tatlandırın ve iyice karıştırın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Add the toasted {crouton.name} and toss gently. Let stand 3–5 minutes so the bread absorbs the dressing but retains some crunch. Dust remaining {spice.name} over the top just before serving.",
        translations: {
          tr: {
            template:
              "Kızartılmış {crouton.name} ekleyin ve hafifçe karıştırın. Ekmeğin sosu emip biraz çıtırlığını koruması için 3–5 dakika bekletin. Servis etmeden hemen önce kalan {spice.name} üzerine serpin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["crunch", "fresh", "acid", "fat", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{crouton.name} ve {spice.name} ile fattoush",
      },
    },
  },
  {
    id: "panzanella",
    nameTemplate: "Panzanella with {bread.name} & {herb.name}",
    foodType: "salad",
    primaryCookingLocation: "no-cook",
    servings: 3,
    activeMinutes: 20,
    totalMinutes: 35,
    slots: [
      slot("bread", "Stale bread", true, "bread", [
        option("bread", 200, "g", "stale, torn into rough 3–4 cm chunks"),
        option("flatbread", 3, "piece", "stale, torn into rough chunks", 2),
      ]),
      slot("tomato", "Ripe tomato", true, "tomato", [
        option("tomato", 4, "piece", "roughly chopped into 2 cm pieces"),
      ]),
      slot("herb", "Fresh herb", true, "basil", [
        option("basil", 20, "g", "leaves roughly torn", 3),
        option("parsley", 20, "g", "roughly chopped", 2),
      ]),
      slot("allium", "Allium", true, "red-onion", [
        option("red-onion", 0.5, "piece", "very thinly sliced", 3),
        option("scallion", 3, "piece", "thinly sliced", 2),
      ]),
      slot("cucumber", "Cucumber (optional)", false, "cucumber", [
        option("cucumber", 150, "g", "roughly diced"),
      ]),
      slot("acid", "Acid", true, "red-wine-vinegar", [
        option("red-wine-vinegar", 2, "tbsp", undefined, 3),
        option("lemon", 2, "piece", "juiced", 2),
      ]),
      slot("oil", "Fat", true, "olive-oil", [option("olive-oil", 4, "tbsp")]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "soak-bread",
        template:
          "Place {bread.name} in a large bowl. Squeeze roughly half the {tomato.name} pieces firmly over the bread to release their juices directly onto it. Add {acid.name} and 2 tbsp {oil.name}, season generously with {salt.name}, and toss well. Leave to soak for 15 minutes — the bread will soften and absorb the liquid.",
        translations: {
          tr: {
            template:
              "{bread.name} büyük bir kaseye koyun. {tomato.name} parçalarının yaklaşık yarısını suyunu doğrudan ekmeğin üzerine bırakmak için kuvvetlice sıkın. {acid.name} ve 2 yemek kaşığı {oil.name} ekleyin, {salt.name} ile bol miktarda tatlandırın ve iyice karıştırın. 15 dakika ıslatmaya bırakın — ekmek yumuşayacak ve sıvıyı emecektir.",
          },
        },
      },
      {
        id: "prep-salad",
        template:
          "Add the remaining {tomato.name}, {allium.name}, and {cucumber.name} if using to the bowl.",
        translations: {
          tr: {
            template:
              "Kalan {tomato.name}, {allium.name} ve kullanıyorsanız {cucumber.name} kaseye ekleyin.",
          },
        },
      },
      {
        id: "dress",
        template:
          "Drizzle over the remaining {oil.name}. Season with {black-pepper.name} and more {salt.name} if needed, and toss gently to combine.",
        translations: {
          tr: {
            template:
              "Kalan {oil.name} üzerine gezdirin. {black-pepper.name} ve gerekiyorsa daha fazla {salt.name} ekleyin ve hafifçe karıştırarak birleştirin.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Scatter {herb.name} over the top and let the salad rest a further 5 minutes before serving — panzanella improves as the bread softens fully and the juices meld.",
        translations: {
          tr: {
            template:
              "Üzerine {herb.name} serpin ve servis etmeden önce salatayı 5 dakika daha dinlendirin — ekmek tamamen yumuşadıkça ve sular karıştıkça panzanella iyileşir.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "fresh", "acid", "fat", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{bread.name} ve {herb.name} ile panzanella",
      },
    },
  },
  {
    id: "gado-gado",
    nameTemplate: "Gado gado with {protein.name} & {green.name}",
    foodType: "salad",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 20,
    totalMinutes: 25,
    slots: [
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 300, "g", "firm, pressed and cut into 2 cm cubes", 3),
        option("tempeh", 200, "g", "cut into 1 cm slices", 2),
      ]),
      slot("sauce", "Peanut sauce base", true, "peanut-butter", [
        option("peanut-butter", 4, "tbsp"),
      ]),
      slot("green", "Steamed green", true, "bok-choy", [
        option("bok-choy", 200, "g", "halved lengthways", 3),
        option("snap-peas", 150, "g", undefined, 2),
      ]),
      slot("fresh", "Raw vegetable", true, "cucumber", [
        option("cucumber", 150, "g", "sliced into 0.5 cm rounds"),
      ]),
      slot("soy", "Soy sauce", true, "soy-sauce", [
        option("soy-sauce", 2, "tbsp"),
      ]),
      slot("acid", "Acid", true, "lime", [
        option("lime", 2, "piece", "juiced", 3),
        option("lemon", 1, "piece", "juiced", 2),
        option("rice-vinegar", 1, "tbsp", undefined, 1),
      ]),
      slot("chili", "Heat (optional)", false, "chili-flakes", [
        option("chili-flakes", 0.5, "tsp"),
        option("sriracha", 1, "tsp"),
      ]),
      slot("oil", "Cooking fat", true, "sesame-oil", [
        option("sesame-oil", 1, "tbsp"),
        option("olive-oil", 1, "tbsp", undefined, 2),
      ]),
      pantry.salt,
      pantry.water(60),
    ],
    steps: [
      {
        id: "make-sauce",
        template:
          "Whisk {sauce.name}, {soy.name}, {acid.name}, and {water.name} in a small bowl until smooth. The sauce should pour easily — add more water a splash at a time if too thick. Add {chili.name} if using and taste; adjust with more lime or soy as needed.",
        translations: {
          tr: {
            template:
              "Küçük bir kasede {sauce.name}, {soy.name}, {acid.name} ve {water.name} pürüzsüz olana kadar çırpın. Sos kolayca akmalıdır — çok koyuysa biraz biraz su ekleyin. Kullanıyorsanız {chili.name} ekleyin ve tadın; gerekirse daha fazla misket limonu veya soya sosu ile ayarlayın.",
          },
        },
      },
      {
        id: "cook-protein",
        template:
          "Heat {oil.name} in a non-stick pan over medium-high heat. Add {protein.name} and cook 3–4 minutes per side until golden and crisp on the outside. Season lightly with {salt.name} and set aside.",
        translations: {
          tr: {
            template:
              "Yapışmaz bir tavada {oil.name} orta-yüksek ateşte ısıtın. {protein.name} ekleyin ve her iki tarafı dışı altın rengi ve çıtır olana kadar 3–4 dakika pişirin. Hafifçe {salt.name} ile tatlandırın ve bir kenara koyun.",
          },
        },
      },
      {
        id: "steam-greens",
        template:
          "Bring a small amount of water to a boil in a saucepan fitted with a steamer basket. Steam {green.name} for 2–3 minutes until just tender but still bright. Alternatively, blanch in boiling salted water for 90 seconds, then drain.",
        translations: {
          tr: {
            template:
              "Buharlık sepetli bir tencerede az miktarda suyu kaynatın. {green.name} sadece yumuşak ama parlak kalana kadar 2–3 dakika buharda pişirin. Alternatif olarak, kaynayan tuzlu suda 90 saniye haşlayın, ardından süzün.",
          },
        },
      },
      {
        id: "assemble",
        template:
          "Arrange {green.name}, {fresh.name}, and {protein.name} on plates or a large platter. Pour {sauce.name} generously over everything. Serve remaining sauce on the side.",
        translations: {
          tr: {
            template:
              "{green.name}, {fresh.name} ve {protein.name} tabaklara veya büyük bir servis tabağına yerleştirin. {sauce.name} bol miktarda her şeyin üzerine dökün. Kalan sosu yanında servis edin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["protein", "fat", "fresh", "umami", "acid"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{protein.name} ve {green.name} ile gado gado",
      },
    },
  },
  {
    id: "greek-salad",
    nameTemplate: "Greek salad with {cheese.name} & {olive.name}",
    foodType: "salad",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 15,
    slots: [
      slot("cheese", "Cheese", true, "feta", [
        option("feta", 120, "g", "broken into rough chunks"),
      ]),
      slot("olive", "Olives", true, "olives", [option("olives", 80, "g")]),
      slot("tomato", "Tomato", true, "tomato", [
        option("tomato", 3, "piece", "cut into wedges"),
      ]),
      slot("cucumber", "Cucumber", true, "cucumber", [
        option(
          "cucumber",
          200,
          "g",
          "cut into rough 2 cm chunks, seeds scooped out",
        ),
      ]),
      slot("allium", "Allium", true, "red-onion", [
        option("red-onion", 0.5, "piece", "very thinly sliced", 3),
        option("scallion", 3, "piece", "thinly sliced", 2),
      ]),
      slot("herb", "Fresh herb (optional)", false, "basil", [
        option("basil", 10, "g", "leaves roughly torn", 3),
        option("parsley", 10, "g", "roughly chopped", 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("red-wine-vinegar", 1, "tbsp", undefined, 2),
      ]),
      slot("oil", "Fat", true, "olive-oil", [option("olive-oil", 3, "tbsp")]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "prep-vegetables",
        template:
          "Combine {tomato.name}, {cucumber.name}, and {allium.name} in a large bowl.",
        translations: {
          tr: {
            template:
              "Büyük bir kasede {tomato.name}, {cucumber.name} ve {allium.name} karıştırın.",
          },
        },
      },
      {
        id: "dress",
        template:
          "Squeeze {acid.name} over the vegetables, drizzle with {oil.name}, season with {salt.name} and {black-pepper.name}, and toss gently.",
        translations: {
          tr: {
            template:
              "Sebzelerin üzerine {acid.name} sıkın, {oil.name} gezdirin, {salt.name} ve {black-pepper.name} ile tatlandırın ve hafifçe karıştırın.",
          },
        },
      },
      {
        id: "finish",
        template:
          "Scatter {olive.name} and {cheese.name} over the top. If using {herb.name}, add it now. Serve immediately — Greek salad is best eaten within minutes of dressing.",
        translations: {
          tr: {
            template:
              "Üzerine {olive.name} ve {cheese.name} serpin. {herb.name} kullanıyorsanız şimdi ekleyin. Hemen servis edin — Yunan salatası soslandıktan dakikalar içinde yenildiğinde en lezzetlidir.",
          },
        },
      },
    ],
    targetBalanceRoles: ["salt", "acid", "fresh", "fat", "umami"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
    translations: {
      tr: {
        nameTemplate: "{cheese.name} ve {olive.name} ile Yunan salatası",
      },
    },
  },
];
